import { useState } from 'react';
import { Check, GraduationCap, RefreshCw } from 'lucide-react';
import { API } from '../lib/constants';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

export const JuniorTrialForm = () => {
  const { t, language } = useLanguage();
  const jt = t.academyPage.juniorTrial;
  const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri'];
  const dayMap = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday' };

  const [formData, setFormData] = useState({
    parent_name: '', parent_email: '', parent_phone: '',
    child_name: '', child_age: '',
    days: [], preferred_time: '',
    experience_level: '', experience_details: '',
    notes: '', consent_given: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleDay = (key) => {
    setFormData(prev => ({
      ...prev,
      days: prev.days.includes(key) ? prev.days.filter(d => d !== key) : [...prev.days, key]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent_given) { toast.error(jt.errorConsent); return; }
    if (formData.days.length === 0) { toast.error(jt.errorDays); return; }
    const ageNum = parseInt(formData.child_age, 10);
    if (isNaN(ageNum) || ageNum < 4 || ageNum > 17) { toast.error(jt.errorAge); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/junior-trial-inquiry`, {
        parent_name: formData.parent_name,
        parent_email: formData.parent_email,
        parent_phone: formData.parent_phone,
        child_name: formData.child_name,
        child_age: ageNum,
        available_days: formData.days.map(k => dayMap[k]),
        preferred_time: formData.preferred_time,
        experience_level: formData.experience_level,
        experience_details: formData.experience_details || null,
        notes: formData.notes || null,
        consent_given: formData.consent_given,
        language
      });
      setSubmitted(true);
    } catch (error) {
      const message = error.response?.data?.detail || jt.errorGeneric;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass-dark rounded-3xl p-10 md:p-14 text-center" data-testid="junior-trial-success">
        <div className="w-20 h-20 bg-[#5B7FA8]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#5B7FA8]/40">
          <Check className="w-10 h-10 text-[#38BDF8]" />
        </div>
        <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 uppercase">{jt.successTitle}</h3>
        <p className="text-lg text-slate-300 max-w-xl mx-auto">{jt.successMessage}</p>
      </div>
    );
  }

  return (
    <div className="glass-dark rounded-3xl p-8 md:p-12">
      <div className="mb-8">
        <h3 className="text-3xl md:text-4xl font-bold text-white mb-2 uppercase">{jt.formTitle}</h3>
        <p className="text-sm font-semibold text-[#38BDF8] uppercase tracking-wider">{jt.juniorsOnly}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Parent Section */}
        <div>
          <p className="text-xs font-bold text-[#5B7FA8] uppercase tracking-widest mb-4">{jt.parentSection}</p>
          <div className="space-y-6">
            <div>
              <label htmlFor="jt-parent-name" className="block text-sm font-semibold text-slate-300 mb-2">{jt.parentName} *</label>
              <input type="text" id="jt-parent-name" required value={formData.parent_name}
                onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                placeholder={jt.parentNamePh} data-testid="jt-parent-name-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="jt-parent-email" className="block text-sm font-semibold text-slate-300 mb-2">{jt.parentEmail} *</label>
                <input type="email" id="jt-parent-email" required value={formData.parent_email}
                  onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                  placeholder={jt.parentEmailPh} data-testid="jt-parent-email-input" />
              </div>
              <div>
                <label htmlFor="jt-parent-phone" className="block text-sm font-semibold text-slate-300 mb-2">{jt.parentPhone} *</label>
                <input type="tel" id="jt-parent-phone" required value={formData.parent_phone}
                  onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                  placeholder={jt.parentPhonePh} data-testid="jt-parent-phone-input" />
              </div>
            </div>
          </div>
        </div>

        {/* Child Section */}
        <div>
          <p className="text-xs font-bold text-[#5B7FA8] uppercase tracking-widest mb-4">{jt.childSection}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="jt-child-name" className="block text-sm font-semibold text-slate-300 mb-2">{jt.childName} *</label>
              <input type="text" id="jt-child-name" required value={formData.child_name}
                onChange={(e) => setFormData({ ...formData, child_name: e.target.value })}
                placeholder={jt.childNamePh} data-testid="jt-child-name-input" />
            </div>
            <div>
              <label htmlFor="jt-child-age" className="block text-sm font-semibold text-slate-300 mb-2">{jt.childAge} *</label>
              <input type="number" id="jt-child-age" required min="4" max="17" value={formData.child_age}
                onChange={(e) => setFormData({ ...formData, child_age: e.target.value })}
                placeholder={jt.childAgePh} data-testid="jt-child-age-input" />
            </div>
          </div>
        </div>

        {/* Availability Section */}
        <div>
          <p className="text-xs font-bold text-[#5B7FA8] uppercase tracking-widest mb-4">{jt.availabilitySection}</p>
          <label className="block text-sm font-semibold text-slate-300 mb-2">{jt.daysLabel} *</label>
          <p className="text-xs text-slate-400 mb-3">{jt.daysHint}</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {dayKeys.map(k => {
              const active = formData.days.includes(k);
              return (
                <button type="button" key={k} onClick={() => toggleDay(k)} data-testid={`jt-day-${k}`}
                  className={`px-4 py-2.5 rounded-xl font-semibold text-sm transition-all border-2 ${
                    active
                      ? 'bg-[#5B7FA8] text-white border-[#5B7FA8] shadow-lg shadow-[#5B7FA8]/40'
                      : 'bg-white/5 text-slate-300 border-white/10 hover:border-[#5B7FA8]/60'
                  }`}>
                  {jt.days[k]}
                </button>
              );
            })}
          </div>
          <label htmlFor="jt-time" className="block text-sm font-semibold text-slate-300 mb-2">{jt.timeLabel} *</label>
          <p className="text-xs text-slate-400 mb-3">{jt.timeHint}</p>
          <select id="jt-time" required value={formData.preferred_time}
            onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
            data-testid="jt-time-select">
            <option value="">—</option>
            <option value="14:00">{jt.time1400}</option>
            <option value="15:00">{jt.time1500}</option>
            <option value="16:00">{jt.time1600}</option>
            <option value="17:00">{jt.time1700}</option>
          </select>
        </div>

        {/* Experience Section */}
        <div>
          <p className="text-xs font-bold text-[#5B7FA8] uppercase tracking-widest mb-4">{jt.experienceSection}</p>
          <div className="space-y-3 mb-4">
            {[
              { val: 'none', label: jt.expNone },
              { val: 'some', label: jt.expSome },
              { val: 'padel', label: jt.expPadel },
            ].map(opt => (
              <label key={opt.val} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                formData.experience_level === opt.val
                  ? 'border-[#5B7FA8] bg-[#5B7FA8]/10'
                  : 'border-white/10 bg-white/5 hover:border-[#5B7FA8]/40'
              }`}>
                <input type="radio" name="jt-experience" required value={opt.val}
                  checked={formData.experience_level === opt.val}
                  onChange={(e) => setFormData({ ...formData, experience_level: e.target.value })}
                  className="w-4 h-4 accent-[#5B7FA8]"
                  data-testid={`jt-exp-${opt.val}`} />
                <span className="text-sm text-slate-200">{opt.label}</span>
              </label>
            ))}
          </div>
          <label htmlFor="jt-exp-details" className="block text-sm font-semibold text-slate-300 mb-2">{jt.expDetailsLabel}</label>
          <input type="text" id="jt-exp-details" value={formData.experience_details}
            onChange={(e) => setFormData({ ...formData, experience_details: e.target.value })}
            placeholder={jt.expDetailsPh} data-testid="jt-exp-details-input" />
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="jt-notes" className="block text-sm font-semibold text-slate-300 mb-2">{jt.notesLabel}</label>
          <textarea id="jt-notes" rows={3} value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder={jt.notesPh} data-testid="jt-notes-input"></textarea>
        </div>

        {/* Consent + Privacy */}
        <div className="bg-white/5 p-6 rounded-xl border border-white/10">
          <div className="flex items-start gap-3">
            <input type="checkbox" id="jt-consent" required checked={formData.consent_given}
              onChange={(e) => setFormData({ ...formData, consent_given: e.target.checked })}
              className="mt-1 w-5 h-5 rounded border-white/20 accent-[#5B7FA8]"
              data-testid="jt-consent-checkbox" />
            <label htmlFor="jt-consent" className="text-sm text-slate-300 leading-relaxed">{jt.consent} *</label>
          </div>
        </div>
        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
          <p className="text-xs text-slate-400 leading-relaxed">{jt.privacyNotice}</p>
        </div>
        <p className="text-sm text-slate-400 italic">{jt.requiredFields}</p>

        <button type="submit" disabled={loading || !formData.consent_given}
          className="athletic-button athletic-button-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="jt-submit-btn">
          {loading ? (<><RefreshCw className="w-5 h-5 animate-spin" />{jt.sending}…</>) : (<><GraduationCap className="w-5 h-5" />{jt.submit}</>)}
        </button>
      </form>
    </div>
  );
};

// Academy Page Component
